import React, { createContext, useContext, useEffect, ReactNode, useState, useCallback } from 'react';
import { getMessaging, onMessage, MessagePayload } from 'firebase/messaging';
import { HeartIcon, ShareIcon, CouponIcon, NotificationsGiftIcon } from '../pages/Home/Coupons/icons';
import { db, Timestamp } from '../firebase';
import { collection, getDocs, query, where, doc, setDoc, updateDoc, limit, getDoc, onSnapshot } from 'firebase/firestore';
import { NotificationData } from '../pages/Home/Coupons/types';
import { useCouponsAppData } from './CouponsAppDataContext';

interface FirestoreNotification {
    id: string;
    userId: number;
    title: string;
    description: string;
    iconType: 'like' | 'share' | 'birthday' | 'coupon';
    read: boolean;
    couponId?: string;
    communityId?: number;
    createdAt: any;
    messageId?: string;
    likerUserId?: string;
}

interface NotificationsContextType {
    notifications: NotificationData[];
    isLoading: boolean;
    markAsRead: (notificationId: string) => Promise<void>;
    markAllAsRead: () => Promise<void>;
    filterBlockedUsers: (senderUserId: string, receiverUserIds: string[]) => Promise<string[]>;
    filterByCustomerPreferences: (receiverUserIds: string[]) => Promise<string[]>;
    filterUsersForNotification: (senderUserId: string, receiverUserIds: string[]) => Promise<string[]>;
    refreshNotifications: () => Promise<void>;
    sendLikeNotification: (couponId: string, couponTitle: string, postingUserId: string, likerName: string, communityId?: number) => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

interface NotificationsProviderProps {
    children: ReactNode;
}

const isCouponNotification = (payload: MessagePayload | null): boolean => {
    if (!payload?.data) {
        return false;
    }
    return payload.data.Type === 'CouponNotificationType';
};

const getNotificationIcon = (iconType: 'like' | 'share' | 'birthday' | 'coupon'): React.FC<any> => {
    switch (iconType) {
        case 'like':
            return HeartIcon;
        case 'share':
            return ShareIcon;
        case 'birthday':
            return NotificationsGiftIcon;
        case 'coupon':
        default:
            return CouponIcon;
    }
};

const getIconBgColor = (iconType: 'like' | 'share' | 'birthday' | 'coupon'): string => {
    switch (iconType) {
        case 'like':
            return '#D6F3FF';
        case 'share':
            return '#D6F3FF';
        case 'birthday':
            return '#FEF9C3';
        case 'coupon':
        default:
            return '#DCFCE4';
    }
};

const getIconFillColor = (iconType: 'like' | 'share' | 'birthday' | 'coupon'): string => {
    switch (iconType) {
        case 'like':
            return '#007EFF';
        case 'share':
            return '#007EFF';
        case 'birthday':
            return '#EAB308';
        case 'coupon':
        default:
            return '#16AA40';
    }
};

const formatTime = (sentTime: number): string => {
    const now = Date.now();
    const diff = now - sentTime;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 1) {
        return 'עכשיו';
    }
    if (minutes < 60) {
        return `לפני ${minutes} דקות`;
    }
    if (hours < 24) {
        return `לפני ${hours} ${hours === 1 ? 'שעה' : 'שעות'}`;
    }
    if (days === 1) {
        return 'יום אחד';
    }
    if (days < 7) {
        return `${days} ימים`;
    }
    const weeks = Math.floor(days / 7);
    return weeks === 1 ? 'שבוע' : `${weeks} שבועות`;
};

const convertFirestoreToNotificationData = (firestoreNotif: FirestoreNotification): NotificationData & { createdAt: number; communityId?: number } => {
    const iconType = firestoreNotif.iconType;
    const icon = getNotificationIcon(iconType);
    const createdAt = firestoreNotif.createdAt?.toDate
        ? firestoreNotif.createdAt.toDate().getTime()
        : firestoreNotif.createdAt?.seconds
            ? firestoreNotif.createdAt.seconds * 1000
            : Date.now();
    const isToday = new Date(createdAt).toDateString() === new Date().toDateString();

    return {
        id: firestoreNotif.id,
        icon,
        iconBg: getIconBgColor(iconType),
        iconFill: getIconFillColor(iconType),
        title: firestoreNotif.title,
        description: firestoreNotif.description,
        time: formatTime(createdAt),
        today: isToday,
        read: firestoreNotif.read,
        couponId: firestoreNotif.couponId,
        createdAt,
        communityId: firestoreNotif.communityId,
    };
};

const getIconTypeFromTitle = (title: string): 'like' | 'share' | 'birthday' | 'coupon' => {
    const lowerTitle = title.toLowerCase();

    if (lowerTitle.includes('לייק') || lowerTitle.includes('אהב') || lowerTitle.includes('like')) {
        return 'like';
    }

    if (lowerTitle.includes('שיתפ') && lowerTitle.includes('איתך')) {
        return 'share';
    }

    if (lowerTitle.includes('היום הגדול') || lowerTitle.includes('יום הולדת')) {
        return 'birthday';
    }

    return 'coupon';
};

const saveNotificationToFirestore = async (
    userId: number,
    payload: MessagePayload,
    fallbackCommunityId: number | null
): Promise<void> => {
    try {
        const { notification, data, messageId } = payload;
        const title = notification?.title || '';
        const description = notification?.body || '';
        const id = messageId || `notification-${Date.now()}`;
        const couponId = data?.CouponId;

        const communityId = data?.CommunityId
            ? Number(data.CommunityId)
            : data?.communityId
                ? Number(data.communityId)
                : fallbackCommunityId;

        const notificationsRef = collection(db, 'notifications');
        const existingQuery = query(
            notificationsRef,
            where('userId', '==', userId),
            where('messageId', '==', id)
        );
        const existingSnapshot = await getDocs(existingQuery);

        if (!existingSnapshot.empty) {
            return;
        }

        const notificationData: any = {
            userId,
            title,
            description,
            iconType: getIconTypeFromTitle(title),
            read: false,
            createdAt: Timestamp.now(),
            messageId: id,
        };

        if (couponId) {
            notificationData.couponId = couponId;
        }
        if (communityId) {
            notificationData.communityId = communityId;
        }

        const newNotificationRef = doc(notificationsRef);
        await setDoc(newNotificationRef, notificationData);
    } catch (error) {
        console.error('Error saving notification to Firestore:', error);
    }
};

const handleCouponNotification = async (
    payload: MessagePayload,
    userId: number,
    refreshNotifications: () => Promise<void>,
    currentCommunityId: number | null
) => {
    try {
        await saveNotificationToFirestore(userId, payload, currentCommunityId);
        await refreshNotifications();
    } catch (error) {
        console.error('Error handling coupon notification:', error);
    }
};

// Send like notification via API (push notification)
const sendLikeNotificationViaAPI = async (
    couponId: string,
    couponTitle: string,
    postingUserId: string,
    likerName: string,
    communityId: number,
    jwtToken?: string
): Promise<{ success: boolean }> => {
    try {
        const apiUrl = `${process.env.REACT_APP_API_MEKOMI_BASE_URL}/sapi/CouponMessages/${communityId}`;
        const apiToken = process.env.REACT_APP_X_ZUMO_APPLICATION;
        const authToken = document.cookie.split('; ').find(row => row.startsWith('mekomeRefresh='))?.split('=')[1];

        const generateGUID = (): string => {
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
                const r = Math.random() * 16 | 0;
                const v = c === 'x' ? r : (r & 0x3 | 0x8);
                return v.toString(16);
            });
        };

        const requestBody = {
            Guid: generateGUID(),
            userList: [Number(postingUserId)],
            couponId: couponId,
            title: `${likerName} אהב/ה את ההטבה שלך!`,
            body: couponTitle,
        };

        console.log('📤 Sending like notification:', requestBody);

        const headers: any = {
            'Content-Type': 'application/json',
            'X-ZUMO-APPLICATION': apiToken || '',
        };

        if (jwtToken) {
            headers['Authorization'] = `Bearer ${jwtToken}`;
        }

        if (authToken) {
            headers['Cookie'] = `mekomeRefresh=${authToken}`;
        }

        const response = await fetch(apiUrl, {
            method: 'POST',
            headers,
            credentials: 'include',
            body: JSON.stringify(requestBody),
        });

        const responseData = await response.json();
        console.log('📥 Like notification response:', responseData);

        const success = response.ok && (responseData.status === 'ok' || response.status === 200);
        return { success };
    } catch (error) {
        console.error('Error sending like notification via API:', error);
        return { success: false };
    }
};

export const sendLikeNotificationToUser = async (
    couponId: string,
    couponTitle: string,
    postingUserId: string,
    likerUserId: string,
    likerName: string,
    communityId?: number | null,
    jwtToken?: string
): Promise<void> => {
    try {
        // Check if posting user has blocked the liker
        const blockedPersonsRef = doc(db, 'blocked_persons', postingUserId);
        const blockedPersonsSnap = await getDoc(blockedPersonsRef);

        if (blockedPersonsSnap.exists()) {
            const data = blockedPersonsSnap.data();
            const blockedUsers = data.blockedUsers || [];
            const isBlocked = blockedUsers.some((blockedUser: any) =>
                blockedUser.userId === likerUserId
            );

            if (isBlocked) {
                console.log('⚠️ Liker is blocked by posting user, skipping like notification');
                return;
            }
        }

        // Check if posting user has push notifications enabled
        const preferencesRef = doc(db, 'customer_preferences', postingUserId);
        const preferencesSnap = await getDoc(preferencesRef);
        let hasPushEnabled = true; // Default to true if no preferences set

        if (preferencesSnap.exists()) {
            const data = preferencesSnap.data();
            hasPushEnabled = data.pushNotifications !== false;
        }

        const notificationsRef = collection(db, 'notifications');
        const existingLikeQuery = query(
            notificationsRef,
            where('userId', '==', Number(postingUserId)),
            where('couponId', '==', couponId),
            where('iconType', '==', 'like'),
            where('likerUserId', '==', likerUserId)
        );

        const existingLikeSnapshot = await getDocs(existingLikeQuery);

        if (!existingLikeSnapshot.empty) {
            console.log('⚠️ Like notification already exists, skipping');
            return;
        }

        const notificationData: any = {
            userId: Number(postingUserId),
            title: `${likerName} אהב/ה את ההטבה שלך!`,
            description: couponTitle,
            iconType: 'like',
            read: false,
            createdAt: Timestamp.now(),
            messageId: `like-${likerUserId}-${couponId}-${Date.now()}`,
            couponId: couponId,
            likerUserId: likerUserId,
        };

        if (communityId) {
            notificationData.communityId = communityId;
        }

        // Save to Firestore (always save for history)
        const newNotificationRef = doc(notificationsRef);
        await setDoc(newNotificationRef, notificationData);
        console.log('✅ Like notification saved to Firestore');

        // Send push notification via API only if user has push enabled
        if (communityId && hasPushEnabled) {
            console.log('📲 Sending like push notification via API...');
            const result = await sendLikeNotificationViaAPI(
                couponId,
                couponTitle,
                postingUserId,
                likerName,
                communityId,
                jwtToken
            );
            if (result.success) {
                console.log('✅ Like push notification sent via API');
            } else {
                console.error('❌ Failed to send like push notification via API');
            }
        } else if (!hasPushEnabled) {
            console.log('📴 User has push notifications disabled, only saved to Firestore');
        }

    } catch (error) {
        console.error('Error sending like notification:', error);
    }
};

export const NotificationsProvider: React.FC<NotificationsProviderProps> = ({ children }) => {
    const appData = useCouponsAppData();
    const [allNotifications, setAllNotifications] = useState<NotificationData[]>([]);
    const [notifications, setNotifications] = useState<NotificationData[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const filterNotificationsByCommunity = useCallback((allNotifs: NotificationData[], communityId: number | undefined): NotificationData[] => {
        if (!communityId) {
            return allNotifs;
        }

        return allNotifs.filter(notif => {
            const notifData = (notif as any).communityId;
            return !notifData || notifData === communityId;
        });
    }, []);

    const loadNotifications = useCallback(async (): Promise<void> => {
        try {
            if (!appData.userId) {
                setIsLoading(false);
                return;
            }

            const notificationsRef = collection(db, 'notifications');
            const notificationsQuery = query(
                notificationsRef,
                where('userId', '==', appData.userId),
                limit(100)
            );

            const snapshot = await getDocs(notificationsQuery);
            const loadedNotifications: NotificationData[] = [];

            snapshot.forEach((doc) => {
                const data = { id: doc.id, ...doc.data() } as FirestoreNotification;
                loadedNotifications.push(convertFirestoreToNotificationData(data));
            });

            loadedNotifications.sort((a, b) => {
                const aTime = (a as any).createdAt || 0;
                const bTime = (b as any).createdAt || 0;
                return aTime - bTime;
            });

            loadedNotifications.reverse();

            const finalNotifications: NotificationData[] = loadedNotifications.map((n: any) => {
                const { createdAt, ...rest } = n;
                return rest;
            });

            setAllNotifications(finalNotifications);

            const filtered = filterNotificationsByCommunity(finalNotifications, appData.currentCommunityId ?? undefined);
            setNotifications(filtered);

            setIsLoading(false);
        } catch (error) {
            console.error('Error loading notifications:', error);
            setIsLoading(false);
        }
    }, [appData.userId, appData.currentCommunityId, filterNotificationsByCommunity]);

    const markAsRead = useCallback(async (notificationId: string): Promise<void> => {
        try {
            const notificationRef = doc(db, 'notifications', notificationId);
            await updateDoc(notificationRef, { read: true });

            setNotifications(prev =>
                prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
            );
            setAllNotifications(prev =>
                prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
            );

            // await loadNotifications(); // Optimization: Don't reload entire list just for read status
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    }, []);

    const markAllAsRead = useCallback(async (): Promise<void> => {
        try {
            if (!appData.userId) return;

            const notificationsRef = collection(db, 'notifications');
            const unreadQuery = query(
                notificationsRef,
                where('userId', '==', appData.userId),
                where('read', '==', false)
            );

            const snapshot = await getDocs(unreadQuery);
            const updatePromises = snapshot.docs.map(doc =>
                updateDoc(doc.ref, { read: true })
            );

            await Promise.all(updatePromises);
            await loadNotifications();
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
    }, [loadNotifications]);

    const filterBlockedUsers = useCallback(async (senderUserId: string, receiverUserIds: string[]): Promise<string[]> => {
        try {
            const allowedUsers: string[] = [];
            for (const receiverUserId of receiverUserIds) {
                try {
                    const blockedPersonsRef = doc(db, 'blocked_persons', receiverUserId);
                    const blockedPersonsSnap = await getDoc(blockedPersonsRef);

                    if (blockedPersonsSnap.exists()) {
                        const data = blockedPersonsSnap.data();
                        const blockedUsers = data.blockedUsers || [];
                        const isSenderBlocked = blockedUsers.some((blockedUser: any) =>
                            blockedUser.userId === senderUserId
                        );

                        if (!isSenderBlocked) {
                            allowedUsers.push(receiverUserId);
                        }
                    } else {
                        allowedUsers.push(receiverUserId);
                    }
                } catch (error) {
                    allowedUsers.push(receiverUserId);
                }
            }
            return allowedUsers;
        } catch (error) {
            return receiverUserIds;
        }
    }, []);

    const filterByCustomerPreferences = useCallback(async (receiverUserIds: string[]): Promise<string[]> => {
        try {
            const allowedUsers: string[] = [];
            for (const receiverUserId of receiverUserIds) {
                try {
                    const preferencesRef = doc(db, 'customer_preferences', receiverUserId);
                    const preferencesSnap = await getDoc(preferencesRef);

                    if (preferencesSnap.exists()) {
                        const data = preferencesSnap.data();
                        const pushNotifications = data.pushNotifications;
                        if (pushNotifications !== false) {
                            allowedUsers.push(receiverUserId);
                        }
                    } else {
                        allowedUsers.push(receiverUserId);
                    }
                } catch (error) {
                    allowedUsers.push(receiverUserId);
                }
            }
            return allowedUsers;
        } catch (error) {
            return receiverUserIds;
        }
    }, []);

    const filterUsersForNotification = useCallback(async (senderUserId: string, receiverUserIds: string[]): Promise<string[]> => {
        try {
            const notBlockedUsers = await filterBlockedUsers(senderUserId, receiverUserIds);
            const allowedUsers = await filterByCustomerPreferences(notBlockedUsers);
            return allowedUsers;
        } catch (error) {
            return receiverUserIds;
        }
    }, [filterBlockedUsers, filterByCustomerPreferences]);

    const sendLikeNotification = useCallback(async (
        couponId: string,
        couponTitle: string,
        postingUserId: string,
        likerName: string,
        communityId?: number
    ): Promise<void> => {
        try {
            const likerUserId = appData.userId ? String(appData.userId) : '';

            if (!likerUserId) {
                return;
            }

            if (likerUserId === postingUserId) {
                return;
            }

            await sendLikeNotificationToUser(
                couponId,
                couponTitle,
                postingUserId,
                likerUserId,
                likerName,
                communityId,
                appData.jwtToken
            );
        } catch (error) {
            console.error('Error sending like notification:', error);
        }
    }, [appData.userId, appData.jwtToken]);

    // Load notifications when user changes
    useEffect(() => {
        if (appData.userId) {
            loadNotifications();
        }
    }, [appData.userId, loadNotifications]);

    // Handle Firebase Messaging (Web)
    useEffect(() => {
        if (!appData.userId) {
            return;
        }

        try {
            const messaging = getMessaging();
            const unsubscribe = onMessage(messaging, async (payload) => {
                if (isCouponNotification(payload)) {
                    console.log('[NotificationsContext] Foreground coupon notification received');
                    await handleCouponNotification(payload, appData.userId!, loadNotifications, appData.currentCommunityId);
                }
            });
            return () => unsubscribe();
        } catch (err) {
            console.log('Firebase messaging not supported or failed to initialize', err);
        }
    }, [appData.userId, appData.currentCommunityId, loadNotifications]);

    // Filter notifications when community changes
    useEffect(() => {
        const filtered = filterNotificationsByCommunity(allNotifications, appData.currentCommunityId ?? undefined);
        setNotifications(filtered);
    }, [appData.currentCommunityId, allNotifications, filterNotificationsByCommunity]);

    return (
        <NotificationsContext.Provider value={{
            notifications,
            isLoading,
            markAsRead,
            markAllAsRead,
            filterBlockedUsers,
            filterByCustomerPreferences,
            filterUsersForNotification,
            refreshNotifications: loadNotifications,
            sendLikeNotification,
        }}>
            {children}
        </NotificationsContext.Provider>
    );
};

export const useNotifications = (): NotificationsContextType => {
    const context = useContext(NotificationsContext);
    if (context === undefined) {
        throw new Error('useNotifications must be used within a NotificationsProvider');
    }
    return context;
};

export default NotificationsContext;
